import { useState } from "react";
import { Badge, Box, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MainLogo from "../../../assets/images/mainlogoNew.png";

/**
 * The body of the sidebar: brand, grouped links, and whoever is signed in.
 *
 * Written once because the desktop drawer and the mobile drawer carried two
 * copies of the same list, which had already drifted apart — different
 * paddings, different gaps, different logo sizes.
 */
const SidebarNav = ({
  groups,
  isRouteActive,
  onNavigate,
  user,
  footerLabel,
  collapsed = false,
  onCollapse,
  onLogout,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);

  const go = (to) => {
    navigate(to);
    onNavigate?.();
  };

  const item = (route) => {
    const active = isRouteActive(route.to);
    const button = (
      <Box
        component="button"
        type="button"
        className={`ep-sidebar-item${active ? " is-active" : ""}`}
        onClick={() => go(route.to)}
      >
        {route.icon && (
          <Box component="span" className="ep-sidebar-icon">
            {route.badge ? (
              <Badge
                color="error"
                badgeContent={route.badge}
                overlap="circular"
              >
                {route.icon}
              </Badge>
            ) : (
              route.icon
            )}
          </Box>
        )}
        <span className="ep-sidebar-label">{route.name}</span>
      </Box>
    );

    // Collapsed, the label is gone, so the name has to arrive some other way.
    return collapsed ? (
      <Tooltip key={route.to} title={route.name} placement="right">
        {button}
      </Tooltip>
    ) : (
      <Box key={route.to}>{button}</Box>
    );
  };

  return (
    <Box className={`ep-sidebar${collapsed ? " is-collapsed" : ""}`}>
      <Box className="ep-sidebar-brand">
        {/* Collapsed, the logo is cropped to its own mark. It used to swap
            to sem-fundo.png, which is still the old i-Moz brand. */}
        <Link to="/" onClick={onNavigate} className="ep-sidebar-logo">
          <img src={MainLogo} alt="Easy Property" />
        </Link>

        {onCollapse && (
          <Box
            component="button"
            type="button"
            className="ep-sidebar-collapse"
            aria-label={t("Headings.SidebarToggle", {
              defaultValue: "Collapse sidebar",
            })}
            onClick={onCollapse}
          >
            <ViewSidebarOutlinedIcon fontSize="small" />
          </Box>
        )}
      </Box>

      <Box className="ep-sidebar-nav">
        {groups.map((group) => (
          <Box key={group.label || "main"} className="ep-sidebar-group">
            {group.label && !collapsed && (
              <Typography component="div" className="ep-sidebar-group-label">
                {group.label}
              </Typography>
            )}
            {group.items.map(item)}
          </Box>
        ))}
      </Box>

      {user?.name && (
        <>
          <Box
            component="button"
            type="button"
            className="ep-sidebar-user"
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            <Box className="ep-sidebar-avatar">{user.initials}</Box>
            <Box sx={{ minWidth: 0, textAlign: "left" }}>
              <Box className="ep-sidebar-user-name">{user.name}</Box>
              {user.role && (
                <Box className="ep-sidebar-user-role">{user.role}</Box>
              )}
            </Box>
          </Box>

          {/* The menu rises from the row it belongs to and keeps its width,
              so it reads as part of the sidebar instead of a card thrown
              over the page beside it. */}
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            marginThreshold={8}
            // Without this MUI freezes the page scroll and pads the body to
            // stand in for the scrollbar, which shunts the whole page
            // sideways for as long as the menu is open.
            disableScrollLock
            slotProps={{
              paper: {
                className: "ep-sidebar-menu",
                // Collapsed, the row is 52px wide; the menu keeps its own
                // width there instead of shrinking to a column of icons.
                style:
                  anchor && !collapsed
                    ? { width: anchor.getBoundingClientRect().width }
                    : undefined,
              },
              list: { sx: { p: 0 } },
            }}
          >
            <Box className="ep-sidebar-menu-head">
              <Box className="ep-sidebar-avatar">{user.initials}</Box>
              <Box sx={{ minWidth: 0 }}>
                <Box className="ep-sidebar-user-name">{user.name}</Box>
                {user.role && (
                  <Box className="ep-sidebar-user-role">{user.role}</Box>
                )}
              </Box>
            </Box>

            <MenuItem
              onClick={() => {
                setAnchor(null);
                onLogout?.();
              }}
            >
              <LogoutRoundedIcon fontSize="small" />
              {t("Profile.Logout", { defaultValue: "Log out" })}
            </MenuItem>
          </Menu>
        </>
      )}

      {!user?.name && footerLabel && !collapsed && (
        <Box className="ep-sidebar-footer">{footerLabel}</Box>
      )}
    </Box>
  );
};

export default SidebarNav;
